import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMask } from "hooks/useMask";
import {
  InputLabel,
  ErrorMessage,
  Button,
  PasswordStrengthMeter
} from "components/UI/atoms";
import { PasswordTooltipHint } from "components/UI/molecules";
import { validationSchema } from "./utils/validationSchema";
import { TermsOfUseModal } from "..";
import "./index.scss";
import { useSignUpSubmit } from "./utils/useSignUpSubmit";

export const SignUpForm = ({ defaultValues = {} }) => {
  const { referralCode } = defaultValues;
  const { isLoading, submitSignUp } = useSignUpSubmit();
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues
  });
  const { maskedValue: maskedPhone, onChange: onPhoneChange } =
    useMask("(###) ### - ####");
  const [passedValidations, setPassedValidations] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const passwordValue = watch("password");

  const onSubmit = async (data) =>
    await submitSignUp({ ...data, referralCode });

  const checkValidations = (password) => {
    let count = 0;
    if (password?.length >= 8) count++;
    if (/[A-Z]/.test(password)) count++;
    if (/[0-9]/.test(password)) count++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) count++;
    setPassedValidations(count);
  };

  const handlePhoneChange = (e) => {
    onPhoneChange(e);
    setValue("phoneNumber", e.target.value);
  };

  useEffect(() => {
    checkValidations(passwordValue);
  }, [passwordValue]);

  return (
    <form
      id="signup-form"
      className="signup-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <SignUpFormField
        placeholder="Email Address"
        label="Email"
        id="email"
        name="email"
        type="email"
        error={errors.email}
        register={register}
        trigger={trigger}
        showValidationIcon={touchedFields.email}
        disabled={!!defaultValues.email}
        disablePopUp
        required
      />

      <SignUpFormField
        value={maskedPhone}
        placeholder="( ### ) ### - ####"
        label="Phone Number"
        id="phoneNumber"
        name="phoneNumber"
        type="tel"
        error={errors.phoneNumber}
        register={register}
        onChange={handlePhoneChange}
        trigger={trigger}
        showValidationIcon={touchedFields.phoneNumber}
        disablePopUp
        required
      />

      <div className="password-container">
        <SignUpFormField
          placeholder="Password"
          label="Password"
          id="password"
          name="password"
          type="password"
          error={errors.password}
          register={register}
          trigger={trigger}
          showValidationIcon={touchedFields.password}
          hintTooltip={<PasswordTooltipHint />}
          disablePopUp
          required
        />

        <PasswordStrengthMeter passwordScore={passedValidations} />
      </div>

      <SignUpFormField
        placeholder="Password"
        label="Confirm Password"
        id="confirmPassword"
        name="confirmPassword"
        type="password"
        error={errors.confirmPassword}
        register={register}
        trigger={trigger}
        showValidationIcon={touchedFields.confirmPassword}
        disablePopUp
        required
      />

      {defaultValues.referralCode && (
        <SignUpFormField
          placeholder=""
          label="Referral Code"
          id="referralCode"
          name="referralCode"
          type="text"
          error={errors.referralCode}
          register={register}
          trigger={trigger}
          disablePopUp
        />
      )}

      <p className="terms-of-use">
        By joining, I agree to GCX's{" "}
        <button
          type="button"
          className="terms-of-use-button"
          onClick={() => setModalIsOpen(true)}
        >
          Terms of Use.
        </button>
      </p>

      <Button
        customClassName="continue-button"
        type="submit"
        disabled={isLoading}
      >
        Continue
      </Button>

      <TermsOfUseModal
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
      />
    </form>
  );
};

const SignUpFormField = ({
  label,
  error,
  register,
  trigger,
  id,
  type = "text",
  showValidationIcon,
  hintTooltip = null,
  disablePopUp,
  ...props
}) => (
  <div className="field-container">
    <InputLabel
      label={label}
      id={id}
      hasError={!!error}
      type={type}
      showValidationIcon={showValidationIcon}
      {...register(id)}
      onBlur={(e) => {
        register(id).onBlur(e);
        trigger(id);
      }}
      hintTooltip={hintTooltip}
      disablePopUp={disablePopUp}
      {...props}
    />
    {error && <ErrorMessage error={error} />}
  </div>
);
